import { useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";

import {
  FrequencyIntervalItem,
  PatientMonitoringType,
  QuestionnaireTypes,
  RelativeDeltaModel,
} from "../../../api";
import { Checkbox } from "../../../components/ui-kit/fields/checkbox";
import { ChooseField } from "../../../components/ui-kit/fields/choose-field";
import { NumberInput } from "../../../components/ui-kit/fields/number-input";
import { Select } from "../../../components/ui-kit/fields/select";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import {
  monitoringTypes,
  questionnaireTypes,
} from "../../../constants/enum-to-text";
import { CountrySelect } from "../../patient/country-select";
import { RegionSelect } from "../../patient/region-select";
import { AdminConsole } from "../admin-console";
import { ClinicsModal } from "../common/clinics-modal";
import { PhysiciansModal } from "../common/physicians-modal";
import { DurationEditor } from "./duration-editor";
import { FrequencyEditor } from "./frequency-editor";

export const QuestionnaireSettingsForm = observer(
  function QuestionnaireSettingsForm({
    fields,
    admin,
    mode,
  }: {
    fields: QuestionnaireSettingsFields;
    admin: AdminConsole;
    mode: "create" | "edit";
  }) {
    const { t } = useLingui();
    return (
      <FormTable dataAdd>
        {mode === "create" && (
          <>
            <FormRow
              label={t`Questionnaire`}
              data={
                <Select
                  attr="questionnaire_id"
                  model={fields}
                  values={questionnaireTypes}
                  required
                />
              }
              mandatory
            />

            <FormRow
              label={t`Monitoring type`}
              data={
                <Select
                  attr="monitoring_type"
                  model={fields}
                  values={monitoringTypes}
                  required
                />
              }
              mandatory
            />

            <FormRow
              label={t`Country`}
              data={
                <CountrySelect
                  attr="country"
                  model={fields}
                  onChange={() => {
                    fields.region = null;
                  }}
                />
              }
            />

            <FormRow
              label={t`Region`}
              data={
                <RegionSelect
                  attr="region"
                  model={fields}
                  country={fields.country ?? undefined}
                />
              }
            />
          </>
        )}

        <FormRow
          label={t`First when active`}
          data={<Checkbox attr="first_when_active" model={fields} />}
        />

        <FormRow
          label={t`Skippable`}
          data={<Checkbox attr="skippable" model={fields} />}
        />

        <FormRow
          label={t`Filling frequency`}
          data={<FrequencyEditor items={fields.filling_frequency} />}
          mandatory={mode === "create"}
        />

        <FormRow
          label={t`First after active`}
          data={
            <DurationEditor
              value={fields.first_after_active}
              onChange={(delta) => {
                fields.first_after_active = delta;
              }}
              units={["years", "months", "days"]}
            />
          }
        />

        <FormRow
          label={t`Stop after N`}
          data={<NumberInput attr="stop_after_n" model={fields} />}
        />

        <FormRow
          label={t`Stop after time from first`}
          data={
            <DurationEditor
              value={fields.stop_after_time_from_first}
              onChange={(delta) => {
                fields.stop_after_time_from_first = delta;
              }}
              units={["years", "months", "days"]}
            />
          }
        />

        {mode === "create" && (
          <>
            <FormRow
              label={t`Clinic`}
              data={
                <ChooseField
                  count={fields["clinic_uuid"] ? 1 : 0}
                  modal={({ onClose }) => {
                    const clinics = admin.createClinics();
                    return (
                      <ClinicsModal
                        title={t`Choose clinic`}
                        preselected={
                          fields["clinic_uuid"] ? [fields["clinic_uuid"]] : []
                        }
                        mode="single"
                        model={clinics}
                        onClose={onClose}
                        onConfirm={({ selected }) => {
                          fields["clinic_uuid"] = selected[0] ?? null;
                        }}
                      />
                    );
                  }}
                />
              }
            />

            <FormRow
              label={t`Physician`}
              data={
                <ChooseField
                  count={fields["physician_uuid"] ? 1 : 0}
                  modal={({ onClose }) => {
                    const physicians = admin.createPhysicians();
                    return (
                      <PhysiciansModal
                        title={t`Choose physician`}
                        preselected={
                          fields["physician_uuid"]
                            ? [fields["physician_uuid"]]
                            : []
                        }
                        mode="single"
                        model={physicians}
                        onClose={onClose}
                        onConfirm={({ selected }) => {
                          fields["physician_uuid"] = selected[0] ?? null;
                        }}
                      />
                    );
                  }}
                />
              }
            />
          </>
        )}
      </FormTable>
    );
  },
);

export interface QuestionnaireSettingsFields {
  questionnaire_id?: QuestionnaireTypes | null;
  monitoring_type?: PatientMonitoringType | null;
  country?: string | null;
  region?: number | null;
  first_when_active: boolean;
  skippable: boolean;
  filling_frequency: FrequencyIntervalItem[];
  first_after_active: RelativeDeltaModel | null;
  stop_after_n?: number | null;
  stop_after_time_from_first: RelativeDeltaModel | null;
  clinic_uuid?: string | null;
  physician_uuid?: string | null;
}
